// Vendors
import React, { ChangeEvent, useEffect, useRef } from 'react';
import { Controller, Control, FieldErrors, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as amplitude from '@amplitude/analytics-browser';
// Services
import RecipientService from '@services/RecipientService';
// Components
import WilmaAsyncSelect, { StringOption } from '@components/WilmaAsyncSelect/WilmaAsyncSelect';
import RecipientToggles from './RecipientToggles';
// Hooks
import { useDisplayLimit } from '@root/components/NewDiscussionThread/hooks/useDisplayLimit';
// Context
import { MessagingState } from '@context/Context';
import { useToast } from '@context/ToastContext';
import { ActionType } from '@context/ActionTypes';
// Types
import { IUser } from '@appTypes/user';
import { LoadingState } from '@appTypes/loadingstates';
import { NewDiscussionThreadForm } from '@components/NewDiscussionThread/NewDiscussionThread';
import { IModalRecipient } from '@appTypes/recipients';
// Payloads
import { IDiscussionThreadSelectPayload } from '@root/services/payloads/servicePayloads';

interface IRecipientsSelectorProps {
    control: Control<NewDiscussionThreadForm>;
    errors: FieldErrors<NewDiscussionThreadForm>;
    setValue: UseFormSetValue<NewDiscussionThreadForm>;
    setMessage: (message: IDiscussionThreadSelectPayload) => void;
    setInfoModalOpen: (isOpen: boolean) => void;
    amplitudeProps: { guid_id: string };
    setRecipientModalOpen: (isOpen: boolean) => void;
    recipientModalOpen: boolean;
}

const RecipientSelector = ({
    control,
    errors,
    setValue,
    setMessage,
    setInfoModalOpen,
    amplitudeProps,
    setRecipientModalOpen,
    recipientModalOpen,
}: IRecipientsSelectorProps) => {
    const {
        state: { message, selectedRecipients, modalRecipients },
        dispatch,
    } = MessagingState();
    const colRef = useRef<HTMLDivElement>(null);
    const { displayLimit } = useDisplayLimit(colRef);
    const { createToast } = useToast();
    const { t } = useTranslation();

    useEffect(() => {
        const users = selectedRecipients.options.map(option => {
            return {
                ...option,
                roleGuid: option.value,
            };
        });
        setValue('recipients', users);
    }, [selectedRecipients.options, setValue]);

    const fetchRecipientOptions = async (inputValue: string) => {
        try {
            const response = await RecipientService.searchRecipients(inputValue);
            const records = response.data.allRecords.map(record => {
                const option: StringOption = {
                    label: record.label,
                    value: record.roleGuid,
                    name: record.name,
                    nameAbbreviation: record.nameAbbreviation,
                    allowMessaging: record.allowSendingMessage,
                    roleType: record.roleType,
                    schoolNames: record.schoolNames,
                    extraInfo: record.extraInfo,
                    isGroup: false,
                };
                return option;
            });
            // hasMore is false since API doesn't support pagination
            return { options: records, hasMore: false };
        } catch (error) {
            createToast({
                title: t('errors.recipientLoadingFailed'),
                toastType: 'danger',
            });
            return { options: [], hasMore: false };
        }
    };

    const mapSingleUsers = (options: readonly StringOption[]) => {
        return options
            .filter(u => !u.isGroup)
            .map(u => {
                const recipient: IUser = { label: u.label, roleGuid: u.value, roleType: u.roleType };
                return recipient;
            });
    };

    const mapGroupUsers = (options: readonly StringOption[], modalRecipientOptions: IModalRecipient[]): IUser[] => {
        const optionGroupLabels = options.filter(option => option.isGroup).map(option => option.value || '');

        const filteredGroupUsers = modalRecipientOptions.filter(option => optionGroupLabels.includes(option.value));

        // map single recipients from group to be an array of single IUser
        return filteredGroupUsers.flatMap(option => {
            const group = option.value;
            return option.options.map(o => ({
                label: o.label,
                roleGuid: o.value,
                roleType: o.roleType,
                groupLabel: group,
            }));
        });
    };

    const handleRecipientSelection = (
        controllerOnChange: (event: IUser[] | ChangeEvent<Element>) => void,
        options: readonly StringOption[]
    ) => {
        // single users in select-component
        const users = mapSingleUsers(options);

        // group users in select-component
        const remainingGroupUsers = mapGroupUsers(options, modalRecipients.modalRecipientOptions);

        setMessage({ ...message, recipients: [...users, ...remainingGroupUsers] });
        amplitude.track('new_message_thread_recipient_changed', {
            ...amplitudeProps,
            recipient_count: users.length,
        });
        controllerOnChange(users);

        dispatch({
            type: ActionType.SET_SELECTED_RECIPIENTS,
            payload: { options: options.map(o => o) },
        });
    };

    const loadAndToggleRecipientModal = async () => {
        if (!recipientModalOpen) {
            dispatch({
                type: ActionType.SET_GENERAL_RECIPIENT_DATA,
                payload: { loadingState: LoadingState.Loading, indexRecords: [] },
            });
            try {
                const response = await RecipientService.getRecipients();
                dispatch({
                    type: ActionType.SET_GENERAL_RECIPIENT_DATA,
                    payload: { ...response, loadingState: LoadingState.Done },
                });
                const stringOptionsToModalRecipients = selectedRecipients.options.map(option => {
                    const groupModalRecipient = modalRecipients.modalRecipientOptions.find(o => o.value === option.value);
                    const modalRecipient = {
                        value: option.value,
                        options: groupModalRecipient ? groupModalRecipient.options : [option],
                        isGroup: option.isGroup,
                    };
                    return modalRecipient;
                });
                dispatch({
                    type: ActionType.SET_MODAL_RECIPIENTS,
                    payload: { modalRecipientOptions: stringOptionsToModalRecipients },
                });
            } catch (e) {
                console.error(e);
                dispatch({
                    type: ActionType.SET_GENERAL_RECIPIENT_DATA,
                    payload: { loadingState: LoadingState.Error, indexRecords: [] },
                });
            }
        }
        setRecipientModalOpen(true);
    };

    const recipientControllerRules = {
        required: t('errors.requiredError.recipientError'),
    };

    return (
        <div className="row">
            <div
                id="new-message-recipients-header-row-label"
                className="col-lg-2">
                <button
                    type="button"
                    className="btn btn-primary bg-secondary action-button"
                    id="select-recipients-button"
                    onClick={loadAndToggleRecipientModal}>
                    {t('selectRecipients.title')}
                </button>
            </div>
            <div
                ref={colRef}
                className="col-lg-10 col-md-12">
                <Controller
                    name="recipients"
                    control={control}
                    rules={recipientControllerRules}
                    render={({ field: { onChange, onBlur } }) => (
                        <>
                            <WilmaAsyncSelect
                                value={selectedRecipients.options}
                                onBlur={onBlur}
                                ariaInvalid={errors.recipients ? 'true' : 'false'}
                                placeholder={t('placeholders.recipients')}
                                loadingMessage={() => t('loading')}
                                loadOptions={fetchRecipientOptions}
                                noOptionsMessage={() => t('noRecipientsFound')}
                                onChange={e => handleRecipientSelection(onChange, e)}
                                recipients={message.recipients}
                                hasErrors={!!errors.recipients}
                                ariaLabel={t('inputs.recipientsList')}
                                displayLimit={displayLimit}
                            />
                            {<p className="error-text">{errors.recipients?.message}</p>}
                        </>
                    )}
                />
                <RecipientToggles
                    setMessage={setMessage}
                    setInfoModalOpen={setInfoModalOpen}
                />
            </div>
        </div>
    );
};

export default RecipientSelector;
